import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ShieldCheck, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import AccountLink from '@/components/generals/account-link'
import Logo from '@/components/generals/logo'
import BagPanel from '@/components/landing/bag-panel'
import BrandSwitch from '@/components/landing/brand-switch'
import PanelShell from '@/components/landing/panel-shell'
import SiteMenu from '@/components/landing/site-menu'
import { useSite } from '@/components/landing/site-provider'

import { NAV_LINKS } from '@/constants/site'

const BAG_PANEL_ID = 'kuro-header-bag'

const SECTION_LINKS = NAV_LINKS.filter((link) => link.href !== '#join')

interface SiteHeaderProps {
    solid?: boolean
}

/*
 * On the home page the hero carries its own header, so this one only slides
 * in once the hero has scrolled past. Other pages pass `solid` to show it
 * from the start, and its section links point back to the home page.
 */
const SiteHeader: FC<SiteHeaderProps> = ({ solid = false }): ReactNode => {
    const {
        brand,
        user,
        bagCount,
        bump
    } = useSite()

    const [scrolledPast, setScrolledPast] = useState(false)
    const visible = solid || scrolledPast
    const [bagOpen, setBagOpen] = useState(false)

    const bagRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let frame = 0

        const update = () => {
            frame = 0

            const hero = document.getElementById('hero')

            setScrolledPast(hero ? hero.getBoundingClientRect().bottom < 72 : window.scrollY > 80)
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [])

    useEffect(() => {
        if (!visible) setBagOpen(false)
    }, [visible])

    useEffect(() => {
        if (!bagOpen) return

        panelRef.current?.focus({ preventScroll: true })

        const onPointerDown = (event: PointerEvent) => {
            if (bagRef.current?.contains(event.target as Node)) return
            setBagOpen(false)
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            setBagOpen(false)
            triggerRef.current?.focus()
        }

        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [bagOpen])

    const bagLabel = bagCount === 0
        ? 'Keranjang, kosong'
        : `Keranjang, ${bagCount} file`

    return (
        <header
            inert={!visible}
            className={cn(
                'fixed inset-x-0 top-0 z-50 px-3 pt-3 transition-[translate,opacity] duration-500 ease-out sm:px-6',
                visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-4 opacity-0'
            )}
        >
            <nav
                aria-label='Navigasi utama'
                className='mx-auto flex max-w-7xl items-center justify-between gap-4 rounded-[1.25rem] border border-cream/10 bg-espresso/80 py-2 pl-5 pr-2 text-cream shadow-[0_18px_40px_-20px_rgb(0_0_0/0.8)] backdrop-blur-xl'
            >
                <Link
                    href={solid ? '/' : '#hero'}
                    aria-label='Hajime, beranda'
                    className='rounded-md text-[1.5rem] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-crema'
                >
                    <Logo />
                </Link>

                <ul className='hidden items-center gap-1 lg:flex'>
                    {SECTION_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={solid ? `/${link.href}` : link.href}
                                className='rounded-full px-3.5 py-2 text-sm font-medium text-cream/70 transition-colors hover:bg-cream/[0.07] hover:text-cream focus-visible:outline-2 focus-visible:outline-crema'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <BrandSwitch className='hidden border-transparent bg-cream/[0.06] backdrop-blur-none sm:inline-flex' />
                    <BrandSwitch variant='compact' className='size-10 border-transparent bg-cream/[0.06] backdrop-blur-none sm:hidden' />

                    {user?.role === 'admin' && (
                        <Link
                            href='/admin'
                            aria-label='Panel admin'
                            title='Panel admin'
                            className='hidden size-10 place-items-center rounded-[0.9rem] bg-cream/[0.06] text-crema transition-colors hover:bg-cream/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema sm:grid'
                        >
                            <ShieldCheck aria-hidden className='size-4' />
                        </Link>
                    )}

                    {!user && (
                        <Link
                            href='/daftar'
                            className='hidden rounded-full bg-cream px-4 py-2.5 text-sm font-semibold text-espresso transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema md:inline-flex'
                        >
                            Daftar gratis
                        </Link>
                    )}

                    <AccountLink className='grid size-10 rounded-[0.9rem] bg-cream/[0.06]' />

                    <div ref={bagRef} className='relative'>
                        <button
                            ref={triggerRef}
                            type='button'
                            onClick={() => setBagOpen((value) => !value)}
                            aria-label={bagLabel}
                            title={bagLabel}
                            aria-haspopup='dialog'
                            aria-expanded={bagOpen}
                            aria-controls={bagOpen ? BAG_PANEL_ID : undefined}
                            className={cn(
                                'relative grid size-10 cursor-pointer place-items-center rounded-[0.9rem] text-cream transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-crema',
                                bagOpen ? 'bg-cream/15' : 'bg-cream/[0.06] hover:bg-cream/10'
                            )}
                        >
                            <ShoppingBag aria-hidden className='size-4' />

                            {bagCount > 0 && (
                                <span
                                    key={bump}
                                    aria-hidden
                                    className='kuro-bump absolute -right-1.5 -top-1.5 grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-crema px-1 text-[0.625rem] font-bold leading-none text-espresso ring-2 ring-espresso'
                                >
                                    {bagCount}
                                </span>
                            )}
                        </button>

                        {bagOpen && (
                            <PanelShell
                                id={BAG_PANEL_ID}
                                label='Keranjang'
                                panelRef={panelRef}
                                className='left-auto right-0 origin-top-right translate-x-0'
                            >
                                <BagPanel onNavigate={() => setBagOpen(false)} />
                            </PanelShell>
                        )}
                    </div>

                    <SiteMenu triggerClassName='bg-cream/[0.06] backdrop-blur-none lg:hidden' />
                </div>
            </nav>
        </header>
    )
}

export default SiteHeader
